// @flow
import { has } from "../utils/helpers/objects";

import { HTTP_METHODS } from "../constants/Constants";

import type { MessageParameters } from "../i18n/types";
import type { RequestOptions } from "../utils/fetch/types";

export type ModularUIErrorResponse = {
  error: {
    id: string,
    properties: {
      [propertyName: string]: string | number,
      ...
    },
  },
};

type RequestResult = { status: number };
type RequestParameters = { [key: string]: any };

const CLIENT_ERROR_CODE = 400;

/**
 * @private
 */
const getErrorInfo = (
  errorResponse: ?ModularUIErrorResponse,
  requestOptions: ?RequestOptions,
) => {
  const defaultErrorInfo = {
    id: "Error.CannotRequestResource",
    defaultMessage: `Cannot request resource ${
      requestOptions?.url ?? ""
    } with method ${requestOptions?.method ?? HTTP_METHODS.GET}`,
    properties: {},
    param: null,
  };

  if (!errorResponse) {
    return defaultErrorInfo;
  }

  if (has(errorResponse, "error")) {
    return {
      ...defaultErrorInfo,
      ...errorResponse.error,
    };
  }

  const [key] = Object.keys(errorResponse);
  if (has(errorResponse[key], "errors")) {
    return {
      ...defaultErrorInfo,
      // $FlowFixMe[prop-missing] - not sure in what case this could happen
      ...errorResponse[key].errors,
    };
  }

  return defaultErrorInfo;
};

/**
 * FetchException
 */
export default class FetchException extends Error {
  request: ?RequestResult | XMLHttpRequest;
  status: number;
  response: ?ModularUIErrorResponse;
  parameters: ?RequestParameters;
  properties: MessageParameters;
  requestOptions: ?RequestOptions;
  id: string;

  constructor(
    errorResponse: ?ModularUIErrorResponse,
    failedRequest?: ?RequestResult | XMLHttpRequest,
    requestOptions?: RequestOptions,
  ) {
    const errorInfo = getErrorInfo(errorResponse, requestOptions);
    super(errorInfo.id);

    this.name = errorInfo.id;
    this.id = errorInfo.id;
    this.request = failedRequest;
    this.requestOptions = requestOptions;

    this.status =
      failedRequest && failedRequest.status
        ? failedRequest.status
        : CLIENT_ERROR_CODE;

    this.response = errorResponse;
    this.properties = errorInfo.properties ?? {};
    this.parameters = errorInfo.param ?? null;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, FetchException);
    }
  }
}
